import { Carousel, cn } from "@au-schein-de/au-ui-library";
import type { FormatedProductPrice } from "@dransay/api";
import type { EmblaCarouselType } from "embla-carousel";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { ExclusiveCarouselHeader } from "./exclusive-carousel-header.component";
import {
	EXCLUSIVE_PDP_CAROUSEL_CONTENT_CLASS,
	EXCLUSIVE_PDP_CAROUSEL_INSET_CLASS,
	EXCLUSIVE_PDP_CAROUSEL_ITEM_CLASS,
	EXCLUSIVE_PDP_CAROUSEL_OPTS,
} from "./exclusive-carousel-nav-button-styles";
import { ExclusiveCarouselNavigation } from "./exclusive-carousel-navigation.component";
import { ExclusiveProductCardItem } from "./exclusive-product-card-item.component";
import { ExclusiveProductsBackgroundPattern } from "./exclusive-products-background-pattern.component";
import { ExclusiveProductsCarouselScrollBar } from "./exclusive-products-carousel-scrollbar.component";
import { getExclusiveCompactLayoutMode } from "./exclusive-products-pdp.logic";
import {
	getExclusivePdpTitleInterpolationOptions,
	getExclusivePdpTitleTranslationKey,
} from "./get-exclusive-pdp-title";

interface ExclusiveProductsCompactSectionProps {
	products: FormatedProductPrice[];
	className?: string;
}

const MOBILE_CARD_CLASS = "shrink-0 w-[164px] min-w-[164px] max-w-[164px]";
const DESKTOP_CARD_CLASS = "md:w-[203px] md:min-w-[203px] md:max-w-[203px]";

function CompactSectionTitle({ title }: { title: string }) {
	return (
		<h2 className="w-fit shrink-0 text-h5 text-gray-700 md:text-body-lg-emphasis">
			{title}
		</h2>
	);
}

function StaticProductGrid({
	products,
	centeredOnMobile,
}: {
	products: FormatedProductPrice[];
	centeredOnMobile: boolean;
}) {
	return (
		<div
			className={cn(
				"flex gap-2 md:gap-3 items-start",
				centeredOnMobile ? "justify-center md:justify-start" : "justify-start",
			)}
		>
			{products.map((product, index) => (
				<ExclusiveProductCardItem
					key={`exclusive-compact-${product.id}`}
					product={product}
					index={index}
					className={cn(MOBILE_CARD_CLASS, DESKTOP_CARD_CLASS)}
				/>
			))}
		</div>
	);
}

function ThreeProductCarousel({
	products,
	mobileTitle,
	desktopTitle,
}: {
	products: FormatedProductPrice[];
	mobileTitle: string;
	desktopTitle: string;
}) {
	const [carouselApi, setCarouselApi] = useState<EmblaCarouselType>();
	const carouselId = useId();
	return (
		<Carousel.Root
			opts={EXCLUSIVE_PDP_CAROUSEL_OPTS}
			buttonPosition="static"
			className="w-full"
			setApi={setCarouselApi}
		>
			<div className={EXCLUSIVE_PDP_CAROUSEL_INSET_CLASS}>
				<ExclusiveCarouselHeader
					mobileTitle={mobileTitle}
					desktopTitle={desktopTitle}
					navigation={<ExclusiveCarouselNavigation api={carouselApi} />}
				/>
			</div>
			<Carousel.Content
				id={carouselId}
				className={cn("items-start", EXCLUSIVE_PDP_CAROUSEL_CONTENT_CLASS)}
			>
				{products.map((product, index) => (
					<Carousel.Item
						key={`exclusive-compact-carousel-${product.id}`}
						className={cn(
							"basis-auto",
							EXCLUSIVE_PDP_CAROUSEL_ITEM_CLASS,
							MOBILE_CARD_CLASS,
							DESKTOP_CARD_CLASS,
						)}
						full={false}
					>
						<ExclusiveProductCardItem
							product={product}
							index={index}
							className="w-full"
						/>
					</Carousel.Item>
				))}
			</Carousel.Content>
			<div className={EXCLUSIVE_PDP_CAROUSEL_INSET_CLASS}>
				<ExclusiveProductsCarouselScrollBar
					api={carouselApi}
					controlsId={carouselId}
					className="mt-4"
				/>
			</div>
		</Carousel.Root>
	);
}

export function ExclusiveProductsCompactSection({
	products,
	className,
}: ExclusiveProductsCompactSectionProps) {
	const { t } = useTranslation("product");
	const count = products.length;
	const mobileTitle = t(
		getExclusivePdpTitleTranslationKey(count, true),
		getExclusivePdpTitleInterpolationOptions(count, true),
	);
	const desktopTitle = t(
		getExclusivePdpTitleTranslationKey(count, false),
		getExclusivePdpTitleInterpolationOptions(count, false),
	);
	const compactLayoutMode = getExclusiveCompactLayoutMode(count);
	const isCarouselLayout = compactLayoutMode === "carousel";
	return (
		<div
			className={cn(
				"relative mb-0 md:mb-6 w-full overflow-hidden bg-gray-200 pt-4 pb-6",
				"md:rounded-lg md:py-6",
				!isCarouselLayout && "px-4 md:px-10",
				className,
			)}
			data-testid="exclusive-products-pdp-section"
		>
			<ExclusiveProductsBackgroundPattern productCount={count} />
			<div className="relative flex flex-col gap-4">
				{isCarouselLayout ? (
					<ThreeProductCarousel
						products={products}
						mobileTitle={mobileTitle}
						desktopTitle={desktopTitle}
					/>
				) : (
					<>
						<div className="md:hidden">
							<CompactSectionTitle title={mobileTitle} />
						</div>
						<div className="hidden md:block">
							<CompactSectionTitle title={desktopTitle} />
						</div>
						<StaticProductGrid
							products={products}
							centeredOnMobile={compactLayoutMode === "static-centered"}
						/>
					</>
				)}
			</div>
		</div>
	);
}
